/**
 * Formatprüfung für Telefon-Eingaben.
 *
 * Zweck: Spam-Leads verhindern, bei denen im Telefonfeld Wörter stehen
 * ("test", "keine Angabe", "asdf").
 *
 * WICHTIG — das ist bewusst KEINE Plausibilitätsprüfung. Es wird nur geprüft,
 * aus welchen Zeichen die Eingabe besteht, nicht ob die Nummer in ein bekanntes
 * Nummernschema passt. Grund: libphonenumbers isValidPhoneNumber() lehnt auch
 * echte Nummern ab (getestet: die Servicenummer 0800123456 und je nach Muster
 * ausländische Nummern) — ein abgelehnter echter Lead fällt niemandem auf,
 * ist aber teurer als ein durchgelassener Spam-Lead.
 *
 * Diese Prüfung kann per Konstruktion keine gültige Nummer ablehnen, weil sie
 * über Nummernbereiche und Längen nichts weiß.
 *
 * Erlaubt: Ziffern, Leerzeichen, + - / . ( )
 * Verlangt: mindestens MIN_DIGITS Ziffern
 * Blockt: jeden Buchstaben und jedes andere Zeichen
 */

/** Kürzeste real vorkommende Ortsnummern haben 6–7 Ziffern. */
export const MIN_DIGITS = 6;

// Alles, was in einer geschriebenen Telefonnummer vorkommen darf.
// Bewusst großzügig: "+49 (0) 89 / 12 34 56-12" muss durchgehen.
// \u00A0 = geschütztes Leerzeichen, kommt beim Kopieren aus Webseiten oft mit.
const ALLOWED = /^[0-9+\-/.()\s\u00A0]*$/;

export const MSG_INVALID_CHARS = 'Bitte geben Sie Ihre Telefonnummer nur mit Ziffern ein.';
export const MSG_TOO_SHORT = 'Bitte geben Sie eine vollständige Telefonnummer ein.';

// Unsichtbare Zeichen, die beim Kopieren aus Webseiten, PDFs oder Word
// mitkommen. Der Nutzer sieht im Feld nur Ziffern — würden sie zur Ablehnung
// führen, wäre die Meldung für ihn nicht nachvollziehbar.
const INVISIBLE = /\u200B|\u200C|\u200D|\u2060|\uFEFF|\u00AD/g;

// Typografische Striche. Viele Webseiten schreiben Nummern als "0170–1234567"
// mit Gedankenstrich statt Bindestrich.
const DASHES = /[\u2010\u2011\u2012\u2013\u2014\u2015\u2212\uFE58\uFE63\uFF0D]/g;

// Ziffern aus anderen Schriftsystemen auf ASCII abbilden, damit die Nummer
// erkannt UND für das Callcenter wählbar ist.
const DIGIT_MAPS = [
    [0xff10, 0xff19], // Fullwidth ０-９
    [0x0660, 0x0669], // arabisch-indisch ٠-٩
    [0x06f0, 0x06f9], // erweitert arabisch-indisch ۰-۹
];

function foldDigits(input) {
    let out = '';
    for (const ch of input) {
        const cp = ch.codePointAt(0);
        const range = DIGIT_MAPS.find(([von, bis]) => cp >= von && cp <= bis);
        out += range ? String(cp - range[0]) : ch;
    }
    return out;
}

/**
 * Normalisiert eine Telefoneingabe, bevor sie geprüft wird.
 * Entfernt unsichtbare Zeichen, vereinheitlicht Striche und Ziffern.
 * @param {string} value
 * @returns {string}
 */
export function normalizePhoneInput(value) {
    const raw = value === null || value === undefined ? '' : String(value);
    return foldDigits(raw.replace(INVISIBLE, '').replace(DASHES, '-'));
}

/**
 * @param {string} value Roheingabe aus dem Feld
 * @returns {string|null} Fehlermeldung für setCustomValidity, oder null wenn ok.
 *                        Leere Eingabe gilt als ok — darüber entscheidet das
 *                        required-Attribut, nicht diese Funktion.
 */
export function checkPhoneInput(value) {
    const raw = normalizePhoneInput(value);
    const trimmed = raw.trim();

    if (trimmed === '') return null;

    if (!ALLOWED.test(trimmed)) return MSG_INVALID_CHARS;

    const digits = trimmed.replace(/\D/g, '').length;
    if (digits < MIN_DIGITS) return MSG_TOO_SHORT;

    return null;
}

export default { checkPhoneInput, normalizePhoneInput, MIN_DIGITS, MSG_INVALID_CHARS, MSG_TOO_SHORT };
